/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, GitBranch } from 'lucide-react';
import ConfidenceChip from '@/components/terra/ConfidenceChip';
import EcosystemBadge from '@/components/terra/EcosystemBadge';

const BLOCKER_LABEL = {
  STALLED_PERCEPTION:       'Stalled perception',
  FOLLOWUP_DEPTH_NEEDED:    'Follow-up depth needed',
  FAMILY_ALIGNMENT_PENDING: 'Family alignment pending',
  DECISION_CLARITY_PENDING: 'Decision clarity pending',
  NEEDS_MOVEMENT:           'Needs movement',
};

export default function EcosystemResistancePage() {
  const [data, setData] = useState(null);
  const [ecosystems, setEcosystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(180);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/terra-growth/intelligence/ecosystem-resistance?days=${days}`),
      axios.get(`${API}/terra-growth/ecosystems`),
    ])
      .then(([r, e]) => { setData(r.data); setEcosystems(e.data?.ecosystems || []); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const ecoMap = useMemo(() => ecosystems.reduce((m, e) => { m[e.code] = e; return m; }, {}), [ecosystems]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="ecosystem-resistance-page" className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Intelligence</p>
            <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">Ecosystem Resistance</h1>
            <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1 max-w-2xl">
              Where each ecosystem hesitates, and which extra step it tends to require.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[90, 180, 365].map(d => (
              <button key={d}
                      data-testid={`er-days-${d}`}
                      onClick={() => setDays(d)}
                      className={`text-[11px] rounded-full px-3 py-1 border ${
                        days === d ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]' : 'bg-white text-[#5C5955] border-[#D8D4CE]'
                      }`}>{d}d</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Computing…
          </div>
        ) : !data || data.rows.length === 0 ? (
          <div data-testid="ecosystem-resistance-empty"
               className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            No transitions in this window. Log perception movements to populate.
          </div>
        ) : (
          <>
            <Rollup rollup={data.ecosystem_rollup} ecoMap={ecoMap} />
            <CellsTable rows={data.rows} ecoMap={ecoMap} labels={data.perception_labels} />
          </>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function Rollup({ rollup, ecoMap }) {
  return (
    <section data-testid="er-rollup" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
      {rollup.map(r => (
        <article key={r.ecosystem_code}
                 data-testid={`er-rollup-${r.ecosystem_code}`}
                 className="rounded-2xl border border-[#EBE8E3] bg-white p-4 space-y-2">
          <div className="flex items-center justify-between">
            {ecoMap[r.ecosystem_code]
              ? <EcosystemBadge ecosystem={ecoMap[r.ecosystem_code]} />
              : <span className="text-[11px] text-[#8A8681]">{r.ecosystem_code}</span>}
            <ConfidenceChip code={r.confidence} sample={r.sample_count} compact />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-['Outfit'] tabular-nums text-[#4A5D4E]">{r.velocity_pct}%</span>
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A8681]">forward velocity</span>
          </div>
          <p className="text-[10px] text-[#8A8681]">
            {r.forward_count} forward of {r.sample_count} transitions
          </p>
        </article>
      ))}
    </section>
  );
}

function CellsTable({ rows, ecoMap, labels }) {
  return (
    <section className="rounded-2xl border border-[#EBE8E3] bg-white overflow-hidden">
      <header className="bg-[#F4F2EE] px-4 py-2 flex items-center gap-2">
        <GitBranch className="w-3 h-3 text-[#8A8681]" />
        <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">By ecosystem × from-state</p>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-['Work_Sans']">
          <thead className="text-[10px] uppercase tracking-[0.12em] text-[#8A8681]">
            <tr>
              <th className="px-3 py-2 text-left">Ecosystem</th>
              <th className="px-3 py-2 text-left">From state</th>
              <th className="px-3 py-2 text-right">Sample</th>
              <th className="px-3 py-2 text-right">Velocity</th>
              <th className="px-3 py-2 text-right">Avg dwell</th>
              <th className="px-3 py-2 text-left">Pending</th>
              <th className="px-3 py-2 text-left">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.ecosystem_code}-${r.from_state}-${i}`}
                  data-testid={`er-row-${r.ecosystem_code}-${r.from_state}`}
                  className="border-t border-[#EBE8E3]">
                <td className="px-3 py-2">
                  {ecoMap[r.ecosystem_code]
                    ? <EcosystemBadge ecosystem={ecoMap[r.ecosystem_code]} compact />
                    : <span className="text-[#8A8681]">{r.ecosystem_code}</span>}
                </td>
                <td className="px-3 py-2 text-[#5C5955]">{labels[r.from_state] || r.from_state}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.sample_count}</td>
                <td className="px-3 py-2 text-right tabular-nums text-[#4A5D4E]">{r.velocity_pct}%</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.avg_days_in_state ?? '—'}</td>
                <td className="px-3 py-2">
                  {r.blockers.length === 0
                    ? <span className="text-[10px] text-[#B5B0A8]">—</span>
                    : (
                      <div className="flex flex-wrap gap-1">
                        {r.blockers.map(b => (
                          <span key={b}
                                data-testid={`er-blocker-${b}`}
                                className="text-[9px] uppercase tracking-[0.14em] bg-[#FAF1E1] text-[#5C3D1F] border border-[#E5D6BE] rounded-full px-1.5 py-0.5">
                            {BLOCKER_LABEL[b] || b}
                          </span>
                        ))}
                      </div>
                    )}
                </td>
                <td className="px-3 py-2"><ConfidenceChip code={r.confidence} sample={r.sample_count} compact /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
